/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { Grid, SelectChangeEvent, Typography } from "@mui/material";
import React, { useState } from "react";
import ContentBox from "../ContentBox/ContentBox";
import ButtonLoading from "../Button/ButtonLoading";
import ApiClient from "../../services/ApiClient";
import { enqueueSnackbar } from "notistack";
import ValidatorSelect from "../Validator/ValidatorSelect";
import MenuItem from "@mui/material/MenuItem";
import { productClassNames } from "../../types/AbstractCase";
import ValidatorTextField from "../Validator/ValidatorTextField";

export default function ImportEConsultDossier() {
  const [isLoading, setIsLoading] = useState(false);
  const [productClassShortName, setProductClassShortName] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<number | null>(null);
  const [eConsultDossierId, setEConsultDossierId] = useState<string | null>(null);

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      const response = await ApiClient.post(`/lb/services/import_e_consult_dossier`, {
        body: JSON.stringify({
          productClassShortName: productClassShortName,
          eConsultDossierId: eConsultDossierId,
          accountId: accountId,
        }),
      });

      if (response.success === true) {
        enqueueSnackbar("eAkte wurde erfolgreich importiert.", {
          variant: "custom",
          buttonType: "link",
          buttonLink: response.caseLink,
          buttonTarget: "_self",
          buttonText: "Zum Fall",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
          autoHideDuration: 10000,
        });

        setProductClassShortName(null);
        setEConsultDossierId(null);
        setAccountId(null);
      } else {
        throw new Error();
      }
    } catch (error) {
      enqueueSnackbar("eAkte konnte nicht importiert werden.", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ContentBox headline="eAkte manuell importieren">
      <Grid container spacing={1} sx={{
        alignItems: "center"
      }}>
        <Grid size={12}>
          <Typography sx={{
            margin: "1rem 0"
          }}>
            Hier können eAkten bei denen der Importer kein Produkt zuweisen konnte unter Angabe des passenden Produkts
            manuell angelegt werden.
          </Typography>
        </Grid>
        <Grid size={12}>
          <ValidatorSelect
            label="Account ID"
            value={accountId}
            onChange={(event: SelectChangeEvent) => setAccountId(parseInt(event.target.value as string))}
            helperText={"Gibt an, aus welchem Account die Mailbox abgerufen werden."}
          >
            {[865267, 1007273, 1007274, 1021716].map((accountId) => (
              <MenuItem key={accountId} value={accountId}>
                {accountId}
              </MenuItem>
            ))}
          </ValidatorSelect>
        </Grid>
        <Grid size={12}>
          <ValidatorTextField
            label={"eConsult Dossier ID"}
            name={"eConsultDossierId"}
            onChange={({ target }: any) => {
              setEConsultDossierId(target.value);
            }}
            value={eConsultDossierId ? eConsultDossierId : ""}
          />
        </Grid>
        <Grid size={12}>
          <ValidatorSelect
            label="Produkt"
            value={productClassShortName}
            onChange={(event: SelectChangeEvent) => setProductClassShortName(event.target.value as string)}
            helperText={"Auswahl des Produkt als welches die eAkte importiert werden soll."}
          >
            {[...productClassNames].sort().map((productClassName) => (
              <MenuItem key={productClassName} value={productClassName}>
                {productClassName}
              </MenuItem>
            ))}
          </ValidatorSelect>
        </Grid>
        <Grid
          sx={{
            py: 3
          }}
          size={12}>
          <ButtonLoading
            variant={"contained"}
            onClick={handleSubmit}
            isLoading={isLoading}
            disabled={eConsultDossierId === null || productClassShortName === null}
          >
            eAkte manuell importieren
          </ButtonLoading>
        </Grid>
      </Grid>
    </ContentBox>
  );
}
